import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';

import { AuthContext } from '@/lib/auth-context';
import { SupportNoteData, supportNotesApi } from '@/services';

export const supportNotesKeys = {
  all: ['support-notes'] as const,
  byStudent: (studentId: string) => ['support-notes', 'student', studentId] as const,
  shared: ['support-notes', 'shared'] as const,
};

/** Every row of a register reads this one cached list, so a full roster costs one request. */
export function useSharedSupportNotesQuery() {
  // Read the context directly: outside an AuthProvider (isolated tests) the list simply stays off.
  const user = useContext(AuthContext)?.user;
  return useQuery({
    queryKey: supportNotesKeys.shared,
    queryFn: supportNotesApi.shared,
    staleTime: 60_000,
    enabled: user?.role === 'admin' || user?.role === 'teacher',
  });
}

export function useStudentSharedSupportNotes(studentId: string) {
  const { data = [] } = useSharedSupportNotesQuery();
  return data.find(entry => entry.studentId === studentId)?.notes ?? [];
}

export function useStudentSupportNotesQuery(studentId: string | undefined) {
  return useQuery({
    queryKey: supportNotesKeys.byStudent(studentId ?? ''),
    queryFn: () => supportNotesApi.list(studentId!),
    enabled: !!studentId,
  });
}

export function useCreateSupportNoteMutation(studentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: SupportNoteData) => supportNotesApi.create(studentId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: supportNotesKeys.all }),
  });
}

export function useUpdateSupportNoteMutation(studentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ noteId, data }: { noteId: string; data: Partial<SupportNoteData> }) =>
      supportNotesApi.update(studentId, noteId, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: supportNotesKeys.all }),
  });
}

export function useDeleteSupportNoteMutation(studentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (noteId: string) => supportNotesApi.remove(studentId, noteId),
    onSuccess: () => qc.invalidateQueries({ queryKey: supportNotesKeys.all }),
  });
}
